import { CleApiErrorText } from "@/components/cle-api/CleApiErrorText";
import type { ReductionEvidenceModel } from "@/api/cle-api";
import {
  mergeEvidenceDetailUpdate,
  mergeEvidenceDetailWithOverride,
  useDownloadReduction,
  useReduction,
  useReductionPdf,
  useReclassifyReviewThread,
} from "@/hooks/cle-hooks";
import { showCleApiErrorToast } from "@/lib/showCleApiErrorToast";
import { useEffect, useMemo, useState } from "react";
import { useToaster } from "rsuite";

import {
  getReclassifyLocks,
  getMissingEvidenceFields,
} from "../person/id/Efteruddannelse/utils/approvalCompleteness";
import {
  DocumentClassificationActions,
  REPORTED_EVIDENCE_LOCKED_REASON,
} from "../person/id/Efteruddannelse/DocumentClassificationActions";
import { EvidencePdfViewer } from "../person/id/Efteruddannelse/EvidencePdfViewer";
import { StatusBadge } from "../person/id/Efteruddannelse/StatusBadge";
import type { CourseCertificateDecision } from "../person/id/Efteruddannelse/types";
import { formatDate } from "../person/id/Efteruddannelse/utils/formatters";
import {
  formatEvidenceStatus,
  getEvidenceStatusVariant,
} from "../person/id/Efteruddannelse/utils/evidenceStatus";
import { useObjectUrl } from "../person/id/Efteruddannelse/useObjectUrl";
import {
  DocumentAuditTrailPanel,
  DocumentCommentsPanel,
} from "./DocumentReviewPanels";
import { EvidenceReviewThreadTopPanel } from "./EvidenceReviewThreadTopPanel";
import { ReductionInformation } from "./ReductionInformation";
import { ReviewDetailLayout } from "./ReviewDetailLayout";
import { ReviewInfoCard, ReviewInfoField } from "./ReviewInfoCard";

interface ReductionReviewViewProps {
  userId: string;
  reductionId: string;
  onBack: () => void;
}

export const ReductionReviewView = ({
  userId,
  reductionId,
  onBack,
}: ReductionReviewViewProps) => {
  const { data, isLoading, error } = useReduction({ userId, reductionId });
  const { data: pdfBlob, isLoading: isPdfLoading } = useReductionPdf({
    userId,
    reductionId,
  });
  const toaster = useToaster();
  const { mutate: downloadReduction, isPending: isDownloading } =
    useDownloadReduction();
  const { mutate: reclassifyReduction, isPending: isReclassifying } =
    useReclassifyReviewThread();
  const pdfUrl = useObjectUrl(pdfBlob);
  const [reductionOverride, setReductionOverride] = useState<
    ReductionEvidenceModel | undefined
  >(undefined);

  useEffect(() => {
    setReductionOverride(undefined);
  }, [reductionId]);

  const reduction = useMemo(
    () => mergeEvidenceDetailWithOverride(data, reductionOverride),
    [data, reductionOverride],
  );

  const handleReductionUpdated = (updated: ReductionEvidenceModel) => {
    setReductionOverride((prev) =>
      mergeEvidenceDetailUpdate(data ?? prev, updated),
    );
  };

  const handleReclassify = (decision: CourseCertificateDecision) => {
    reclassifyReduction(
      {
        subjectId: reductionId,
        userId,
        newStatus: decision,
      },
      {
        onSuccess: () => setReductionOverride(undefined),
        onError: (err) => showCleApiErrorToast(toaster, err),
      },
    );
  };

  return (
    <ReviewDetailLayout
      className="p-2"
      onBack={onBack}
      topPanel={
        <EvidenceReviewThreadTopPanel
          reviewThread={reduction?.reviewThread}
          ownerUserId={userId}
        />
      }
      loading={isLoading}
      error={
        error ? (
          <CleApiErrorText
            error={error}
            fallback="Kunne ikke hente reduktion."
          />
        ) : null
      }
      pdfPanel={
        reduction ? (
          <EvidencePdfViewer
            fileName={reduction.fileName ?? "Uploadet dokument"}
            isDownloading={isDownloading}
            isLoading={isPdfLoading}
            onDownload={() => downloadReduction({ userId, reductionId })}
            pdfUrl={pdfUrl}
          />
        ) : null
      }
      auditPanel={<DocumentAuditTrailPanel subjectId={reductionId} />}
      infoPanel={
        reduction ? (
          <>
            <ReviewInfoCard title="Reduktionsdokument">
              <ReviewInfoField
                label="Filnavn"
                value={reduction.fileName ?? "-"}
              />
              <ReviewInfoField
                label="Status"
                value={
                  <StatusBadge
                    pill
                    status={formatEvidenceStatus(reduction.status)}
                    variant={getEvidenceStatusVariant(reduction.status)}
                  />
                }
              />
              <ReviewInfoField
                label="Indberettet"
                value={reduction.isReported ? "Ja" : "Nej"}
              />
              <ReviewInfoField
                label="Oprettet"
                value={formatDate(reduction.createdAt)}
              />
              <ReviewInfoField
                label="Oprettet af"
                value={reduction.createdByUserName ?? "-"}
              />
            </ReviewInfoCard>
            <ReductionInformation
              reduction={reduction}
              onReductionUpdated={handleReductionUpdated}
            />
          </>
        ) : null
      }
      actionsPanel={
        reduction ? (
          <DocumentClassificationActions
            isPending={isReclassifying}
            onReclassify={handleReclassify}
            {...getReclassifyLocks({
              isReported: reduction.isReported,
              missingFields: getMissingEvidenceFields(reduction),
              reportedReason: REPORTED_EVIDENCE_LOCKED_REASON,
            })}
          />
        ) : null
      }
      commentsPanel={
        <DocumentCommentsPanel
          subjectId={reductionId}
          ownerUserId={userId}
          onCommentAdded={() => setReductionOverride(undefined)}
          isReported={reduction?.isReported ?? false}
        />
      }
    />
  );
};
